'use client';

import { CMS_WORKSPACE_UPGRADE_OVERLAY_ID } from '@/cms/overlays/references';
import { UpgradeDialogActionProvider } from '@/components/paid-access/upgrade-dialog-action';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getCmsOverlayClientRuntime } from './client-runtime-registry';
import { CmsOverlayHost } from './cms-overlay-host';
import { createCmsGlobalOverlayState } from './state';
import type {
  CmsLoadedGlobalOverlayState,
  CmsOverlayRenderModel,
  CmsOverlayTriggerPayload,
} from './types';

type CmsGlobalOverlaysContextValue = {
  openOverlay: (overlay: CmsLoadedGlobalOverlayState) => void;
  openOverlayById: (
    overlayId: string,
    payload?: CmsOverlayTriggerPayload
  ) => boolean;
  preloadOverlayById: (overlayId: string) => boolean;
  closeOverlay: () => void;
};

const CmsGlobalOverlaysContext =
  createContext<CmsGlobalOverlaysContextValue | null>(null);

type CmsOverlaySource = {
  locale: string;
  version: 'draft' | 'published';
  previewId?: string;
};

function canLazyLoadOverlaySource(
  overlaySource: CmsOverlaySource | undefined
): overlaySource is CmsOverlaySource & { version: 'published' } {
  return Boolean(
    overlaySource &&
      overlaySource.version === 'published' &&
      !overlaySource.previewId
  );
}

function createCmsOverlayUnavailableError(overlayId: string) {
  return new Error(`CMS overlay "${overlayId}" is not available.`);
}

function createCmsOverlayLoadError(overlayId: string) {
  return new Error(`CMS overlay "${overlayId}" failed to load.`);
}

function createCmsOverlayMissingClientRuntimeError(
  overlay: CmsOverlayRenderModel
) {
  return new Error(
    `CMS overlay "${overlay.id}" has no registered client runtime for type "${overlay.type}".`
  );
}

function createCmsOverlayStateError(overlay: CmsOverlayRenderModel) {
  return new Error(
    `CMS overlay "${overlay.id}" failed to create ${overlay.type} state.`
  );
}

function normalizeCmsOverlayLoadError(error: unknown) {
  return error instanceof Error ? error : new Error(String(error));
}

export function CmsGlobalOverlaysProvider({
  children,
  idlePreloadOverlayIds = [],
  overlays,
  overlaySource,
  previewOverlayId = null,
}: {
  children: ReactNode;
  idlePreloadOverlayIds?: string[];
  overlays: CmsOverlayRenderModel[];
  overlaySource?: {
    locale: string;
    version: 'draft' | 'published';
    previewId?: string;
  };
  previewOverlayId?: string | null;
}) {
  const [overlay, setOverlay] = useState<CmsLoadedGlobalOverlayState | null>(
    null
  );
  const [lazyLoadedOverlays, setLazyLoadedOverlays] = useState<
    CmsOverlayRenderModel[]
  >([]);
  const [overlayLoadError, setOverlayLoadError] = useState<Error | null>(null);
  const overlayLoadPromisesRef = useRef(
    new Map<string, Promise<CmsOverlayRenderModel | null>>()
  );
  const runtimeLoadPromisesRef = useRef(
    new Map<
      string,
      Promise<Awaited<ReturnType<typeof getCmsOverlayClientRuntime>>>
    >()
  );
  const overlayMap = useMemo(
    () =>
      new Map(
        [...overlays, ...lazyLoadedOverlays].map((item) => [item.id, item])
      ),
    [lazyLoadedOverlays, overlays]
  );

  const openOverlay = useCallback(
    (nextOverlay: CmsLoadedGlobalOverlayState) => {
      setOverlay(nextOverlay);
    },
    []
  );

  const loadOverlayRuntimeByType = useCallback((type: string) => {
    const pendingLoad = runtimeLoadPromisesRef.current.get(type);
    if (pendingLoad) {
      return pendingLoad;
    }

    const loadPromise = getCmsOverlayClientRuntime(type).finally(() => {
      runtimeLoadPromisesRef.current.delete(type);
    });
    runtimeLoadPromisesRef.current.set(type, loadPromise);
    return loadPromise;
  }, []);

  const createOverlayState = useCallback(
    async (params: {
      model: CmsOverlayRenderModel;
      payload?: CmsOverlayTriggerPayload;
    }) => {
      const runtime = await loadOverlayRuntimeByType(params.model.type);
      if (!runtime) {
        throw createCmsOverlayMissingClientRuntimeError(params.model);
      }

      const nextOverlay = createCmsGlobalOverlayState({
        ...params,
        runtime,
      });
      if (!nextOverlay) {
        throw createCmsOverlayStateError(params.model);
      }

      return nextOverlay;
    },
    [loadOverlayRuntimeByType]
  );

  const reportOverlayLoadFailure = useCallback((error: unknown) => {
    setOverlayLoadError(() => normalizeCmsOverlayLoadError(error));
  }, []);

  const loadOverlayModelById = useCallback(
    async (overlayId: string) => {
      const matchedOverlay = overlayMap.get(overlayId);
      if (matchedOverlay) {
        return matchedOverlay;
      }

      if (!canLazyLoadOverlaySource(overlaySource)) {
        return null;
      }
      const publishedOverlaySource = overlaySource;

      const pendingLoad = overlayLoadPromisesRef.current.get(overlayId);
      if (pendingLoad) {
        return pendingLoad;
      }

      const loadPromise = (async () => {
        const search = new URLSearchParams({
          locale: publishedOverlaySource.locale,
          version: publishedOverlaySource.version,
          overlayId,
        });
        const response = await fetch(`/api/cms/overlays?${search.toString()}`, {
          credentials: 'same-origin',
          cache: 'no-store',
        });

        if (!response.ok) {
          throw createCmsOverlayLoadError(overlayId);
        }

        const data = (await response.json()) as {
          overlay: CmsOverlayRenderModel | null;
        };
        if (!data.overlay) {
          throw createCmsOverlayUnavailableError(overlayId);
        }

        const overlay = data.overlay;

        setLazyLoadedOverlays((current) =>
          current.some((item) => item.id === overlay.id)
            ? current
            : [...current, overlay]
        );

        return overlay;
      })().finally(() => {
        overlayLoadPromisesRef.current.delete(overlayId);
      });

      overlayLoadPromisesRef.current.set(overlayId, loadPromise);
      return loadPromise;
    },
    [overlayMap, overlaySource]
  );

  const openOverlayById = useCallback(
    (overlayId: string, payload?: CmsOverlayTriggerPayload) => {
      const matchedOverlay = overlayMap.get(overlayId);
      if (!matchedOverlay) {
        if (!canLazyLoadOverlaySource(overlaySource)) {
          reportOverlayLoadFailure(createCmsOverlayUnavailableError(overlayId));
          return false;
        }

        void loadOverlayModelById(overlayId)
          .then(async (overlay) => {
            if (!overlay) {
              throw createCmsOverlayUnavailableError(overlayId);
            }

            const nextOverlay = await createOverlayState({
              model: overlay,
              payload,
            });
            openOverlay(nextOverlay);
          })
          .catch(reportOverlayLoadFailure);
        return true;
      }

      void createOverlayState({
        model: matchedOverlay,
        payload,
      })
        .then((nextOverlay) => {
          openOverlay(nextOverlay);
        })
        .catch(reportOverlayLoadFailure);
      return true;
    },
    [
      createOverlayState,
      loadOverlayModelById,
      openOverlay,
      overlayMap,
      overlaySource,
      reportOverlayLoadFailure,
    ]
  );

  const preloadOverlayById = useCallback(
    (overlayId: string) => {
      if (overlayMap.has(overlayId)) {
        return true;
      }

      if (!canLazyLoadOverlaySource(overlaySource)) {
        reportOverlayLoadFailure(createCmsOverlayUnavailableError(overlayId));
        return false;
      }

      void loadOverlayModelById(overlayId).catch(reportOverlayLoadFailure);
      return true;
    },
    [loadOverlayModelById, overlayMap, overlaySource, reportOverlayLoadFailure]
  );

  const closeOverlay = useCallback(() => {
    setOverlay(null);
  }, []);

  const value = useMemo(
    () => ({
      openOverlay,
      openOverlayById,
      preloadOverlayById,
      closeOverlay,
    }),
    [closeOverlay, openOverlay, openOverlayById, preloadOverlayById]
  );
  const openUpgradeDialog = useCallback(() => {
    openOverlayById(CMS_WORKSPACE_UPGRADE_OVERLAY_ID, {
      reason: 'upgrade-intent',
    });
  }, [openOverlayById]);

  useEffect(() => {
    if (!previewOverlayId || overlay) {
      return;
    }

    openOverlayById(previewOverlayId);
  }, [openOverlayById, overlay, previewOverlayId]);

  useEffect(() => {
    const uniqueOverlayIds = Array.from(new Set(idlePreloadOverlayIds));
    if (uniqueOverlayIds.length === 0) {
      return undefined;
    }

    return scheduleIdleWork(() => {
      for (const overlayId of uniqueOverlayIds) {
        preloadOverlayById(overlayId);
      }
    });
  }, [idlePreloadOverlayIds, preloadOverlayById]);

  if (overlayLoadError) {
    throw overlayLoadError;
  }

  return (
    <CmsGlobalOverlaysContext.Provider value={value}>
      <UpgradeDialogActionProvider onOpenUpgradeDialog={openUpgradeDialog}>
        {children}
      </UpgradeDialogActionProvider>

      <CmsOverlayHost overlay={overlay} onClose={closeOverlay} />
    </CmsGlobalOverlaysContext.Provider>
  );
}

export function CmsOverlayIdlePreload({
  overlayId,
}: {
  overlayId?: string | null;
}) {
  const { preloadOverlayById } = useCmsGlobalOverlays();

  useEffect(() => {
    if (!overlayId) {
      return undefined;
    }

    return scheduleIdleWork(() => {
      preloadOverlayById(overlayId);
    });
  }, [overlayId, preloadOverlayById]);

  return null;
}

export function useCmsGlobalOverlays() {
  const value = useContext(CmsGlobalOverlaysContext);

  if (!value) {
    throw new Error(
      'useCmsGlobalOverlays must be used inside CmsGlobalOverlaysProvider'
    );
  }

  return value;
}

function scheduleIdleWork(callback: () => void) {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const idleWindow = window as Window & {
    requestIdleCallback?: (
      callback: () => void,
      options?: { timeout: number }
    ) => number;
    cancelIdleCallback?: (handle: number) => void;
  };

  if (idleWindow.requestIdleCallback) {
    const handle = idleWindow.requestIdleCallback(callback, { timeout: 3000 });
    return () => {
      idleWindow.cancelIdleCallback?.(handle);
    };
  }

  const timeout = window.setTimeout(callback, 1200);
  return () => {
    window.clearTimeout(timeout);
  };
}
